import moment from 'moment';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { getEmailFileViewerPageUrl } from '@/src/components/project/Inbox2/pages/InboxEntryViewerPage/FileEntryViewerPage/EmailFileEntryViewerPage';
import { FILE_TASK_LINK_KEY, InboxEntryStatus } from '@/src/components/project/Inbox2/types';
import { queryClient } from '@/src/lib/react-query/react-query';
import { RootState } from '@/src/reducers/types';

import { useDuplicateEmailFile } from './api/duplicateEmailFile';
import {
  emailFileActivityLogsKeys,
  useEmailFileActivityLogs,
} from './api/getEmailFileActivityLogs';
import { emailFileCommentsKeys, useEmailFileComments } from './api/getEmailFileComments';
import { emailFileDetailsKeys } from './api/getEmailFileDetails';
import { useMarkMiscellaneousEmailFile } from './api/markMiscellaneousEmailFile';
import { useMarkReadEmailFile } from './api/markReadEmailFile';
import { useRecordEmailFile } from './api/recordEmailFile';
import { useTagEmailFileWorkstream } from './api/tagEmailFileWorkstream';
import { useUpdateEmailFileStatus } from './api/updateEmailFileStatus';

import { emailEntriesKeys } from '../../api/getEmailEntries';
import { emailEntryKeys } from '../../api/getEmailEntry';

export type useEmailFileEntryViewerStoreProps = {
  projectId: string;
  inboxEntryId: number;
  emailId: number;
  fileId: number;
  documentFileId: number;
};

const useEmailFileEntryViewerStore = ({
  projectId,
  inboxEntryId,
  emailId,
  fileId,
  documentFileId,
}) => {
  const loggedUser = useSelector((store: RootState) => store.loggedUser);
  const recordFileMutation = useRecordEmailFile({
    projectId,
    emailId,
    inboxEntryId,
    fileId,
  });
  const markMiscellaneousMutation = useMarkMiscellaneousEmailFile({
    projectId,
    emailId,
    inboxEntryId,
    fileId,
  });
  const markReadMutation = useMarkReadEmailFile({
    projectId,
    emailId,
    inboxEntryId,
    fileId,
  });
  const markReadMutationListener = useMarkReadEmailFile({
    projectId,
    emailId,
    inboxEntryId,
    fileId,
  });
  const updateStatusMutation = useUpdateEmailFileStatus({
    projectId,
    emailId,
    inboxEntryId,
    fileId,
  });
  const updateStatusMutationListener = useUpdateEmailFileStatus({
    projectId,
    emailId,
    inboxEntryId,
    fileId,
  });
  const tagFileWorkstreamMutation = useTagEmailFileWorkstream({
    projectId,
    emailId,
    inboxEntryId,
    fileId,
  });
  const duplicateFileMutation = useDuplicateEmailFile({
    fileId: documentFileId,
    inboxEntryId,
    projectId,
  });

  const handleTaskCreated = useCallback(
    (tasks) => {
      const newTasks = tasks.map((task) => ({
        id: task.id,
        name: task.task,
        status: task.status,
      }));
      const newWorkstreams = tasks.map((task) => ({
        id: task.workstream.id,
        name: task.workstream.workstream,
        color: task.workstream.color,
      }));

      // queryClient.setQueriesData(
      //   [...emailEntriesKeys.lists({ projectId, inboxEntryId })],
      //   (prev: any) => {
      //     return {
      //       ...prev,
      //       pages: prev.pages.map((page) => {
      //         return {
      //           ...page,
      //           data: {
      //             ...page.data,
      //             emails: page.data.emails.map((emailEntry) => {
      //               if (emailEntry.id === emailId) {
      //                 return {
      //                   ...emailEntry,
      //                   tasks: emailEntry.tasks?.length
      //                     ? emailEntry.tasks.map((eetask) => {
      //                         const newTask = newTasks.find((t) => t.id === eetask.id);
      //                         if (newTask) {
      //                           return {
      //                             id: newTask.id,
      //                             name: newTask.task,
      //                             status: newTask.status,
      //                           };
      //                         }

      //                         return eetask;
      //                       })
      //                     : [...(emailEntry.tasks ? emailEntry.tasks : []), ...newTasks],
      //                 };
      //               }
      //               return emailEntry;
      //             }),
      //           },
      //         };
      //       }),
      //     };
      //   }
      // );

      if (
        queryClient.getQueryData([...emailEntryKeys.detail({ emailId, inboxEntryId, projectId })])
      ) {
        queryClient.setQueryData(
          [...emailEntryKeys.detail({ emailId, inboxEntryId, projectId })],
          (prev: any) => {
            return {
              ...prev,
              data: {
                ...prev.data,
                tasks:
                  prev.data.tasks.findIndex((t) => t.id === newTasks[0].id) >= 0
                    ? prev.data.tasks.map((eetask) => {
                        const newTask = newTasks.find((t) => t.id === eetask.id);
                        if (newTask) {
                          return {
                            id: newTask.id,
                            name: newTask.task,
                            status: newTask.status,
                          };
                        }

                        return eetask;
                      })
                    : [...prev.data.tasks, ...newTasks],
                workstreams:
                  prev.data.workstreams.findIndex((ws) => ws.id === newWorkstreams[0].id) >= 0
                    ? prev.data.workstreams
                    : [...prev.data.workstreams, newWorkstreams[0]],
              },
            };
          }
        );
      }

      queryClient.setQueryData(
        [...emailFileDetailsKeys.detail({ projectId, emailId, inboxEntryId, fileId })],
        (prev: any) => {
          return {
            ...prev,
            data: {
              ...prev.data,
              'allow-close': {
                allowed: true,
              },
              'allowed-status-changes': [InboxEntryStatus.Closed],
              tasks:
                prev.data.tasks.findIndex((t) => t.id === newTasks[0].id) >= 0
                  ? prev.data.tasks.map((eetask) => {
                      const newTask = newTasks.find((t) => t.id === eetask.id);
                      if (newTask) {
                        return {
                          id: newTask.id,
                          name: newTask.task,
                          status: newTask.status,
                        };
                      }

                      return eetask;
                    })
                  : [...prev.data.tasks, ...newTasks],
            },
          };
        }
      );

      queryClient.invalidateQueries([
        ...emailFileActivityLogsKeys.detail({ projectId, fileId, emailId, inboxEntryId }),
      ]);
    },
    [projectId, emailId, inboxEntryId, fileId]
  );

  const handleTaskUpdated = useCallback(
    (updatedTask) => {
      // queryClient.setQueriesData(
      //   [...emailEntriesKeys.lists({ projectId, inboxEntryId })],
      //   (prev: any) => {
      //     return {
      //       ...prev,
      //       pages: prev.pages.map((page) => {
      //         return {
      //           ...page,
      //           data: {
      //             ...page.data,
      //             emails: page.data.emails.map((emailEntry) => {
      //               if (emailEntry.id === emailId) {
      //                 return {
      //                   ...emailEntry,
      //                   tasks:
      //                     emailEntry.tasks?.map((eetask) => {
      //                       if (eetask.id === updatedTask.id) {
      //                         return {
      //                           id: updatedTask.id,
      //                           name: updatedTask.task,
      //                           status: updatedTask.status,
      //                         };
      //                       }

      //                       return eetask;
      //                     }) || [],
      //                 };
      //               }
      //               return emailEntry;
      //             }),
      //           },
      //         };
      //       }),
      //     };
      //   }
      // );

      queryClient.setQueryData(
        [...emailFileDetailsKeys.detail({ projectId, emailId, inboxEntryId, fileId })],
        (prev: any) => {
          return {
            ...prev,
            data: {
              ...prev.data,
              tasks: prev.data.tasks.map((eetask) => {
                if (eetask.id === updatedTask.id) {
                  return {
                    id: updatedTask.id,
                    name: updatedTask.task,
                    status: updatedTask.status,
                  };
                }

                return eetask;
              }),
            },
          };
        }
      );

      queryClient.invalidateQueries([
        ...emailFileActivityLogsKeys.detail({ projectId, fileId, emailId, inboxEntryId }),
      ]);
    },
    [projectId, emailId, inboxEntryId, fileId]
  );

  const handleTaskDeleted = useCallback(
    ({ deletedTaskIds }) => {
      queryClient.setQueriesData(
        [...emailEntriesKeys.lists({ projectId, inboxEntryId })],
        (prev: any) => {
          return {
            ...prev,
            pages: prev.pages.map((page) => {
              return {
                ...page,
                data: {
                  ...page.data,
                  emails: page.data.emails.map((emailEntry) => {
                    if (emailEntry.id === emailId) {
                      return {
                        ...emailEntry,
                        tasks:
                          emailEntry.tasks?.filter((task) => !deletedTaskIds.includes(task.id)) ||
                          [],
                      };
                    }
                    return emailEntry;
                  }),
                },
              };
            }),
          };
        }
      );

      queryClient.setQueryData(
        [...emailFileDetailsKeys.detail({ projectId, emailId, inboxEntryId, fileId })],
        (prev: any) => {
          return {
            ...prev,
            data: {
              ...prev.data,
              tasks: prev.data.tasks?.filter((task) => !deletedTaskIds.includes(task.id)) || [],
              shouldWaitSocketUpdate: true,
            },
          };
        }
      );

      queryClient.invalidateQueries([
        ...emailFileActivityLogsKeys.detail({ projectId, fileId, emailId, inboxEntryId }),
      ]);
    },
    [projectId, emailId, inboxEntryId, fileId]
  );

  const handleFileArchived = useCallback(
    ({ newFolderPath, tempBreadCrumbs }) => {
      queryClient.setQueryData(
        [...emailFileDetailsKeys.detail({ projectId, emailId, inboxEntryId, fileId })],
        (prev: any) => {
          return {
            ...prev,
            data: {
              ...prev.data,
              'allow-close': {
                allowed: true,
              },
              'allowed-status-changes': [InboxEntryStatus.Closed],
              'library-location': {
                ...prev.data['library-location'],
                'full-path': newFolderPath,
                tempBreadCrumbs,
              },
            },
          };
        }
      );

      queryClient.invalidateQueries([
        ...emailFileActivityLogsKeys.detail({ projectId, fileId, emailId, inboxEntryId }),
      ]);
    },
    [projectId, emailId, inboxEntryId, fileId]
  );

  const handleCommentAdded = useCallback(
    (response) => {
      queryClient.setQueryData(
        [...emailFileCommentsKeys.detail({ projectId, fileId, emailId, inboxEntryId })],
        (prev: any) => {
          return {
            ...prev,
            data: [...prev.data, response.data.result],
          };
        }
      );

      queryClient.invalidateQueries([
        ...emailFileActivityLogsKeys.detail({ projectId, fileId, emailId, inboxEntryId }),
      ]);
    },
    [projectId, emailId, inboxEntryId, fileId]
  );

  const handleActivityLogsUpdated = useCallback(() => {
    queryClient.invalidateQueries([
      ...emailFileActivityLogsKeys.detail({ projectId, fileId, emailId, inboxEntryId }),
    ]);
  }, [projectId, emailId, inboxEntryId, fileId]);

  const handleCopyFielUrl = useCallback(() => {
    return getEmailFileViewerPageUrl({ projectId, inboxEntryId, emailId, fileId });
  }, [inboxEntryId, projectId, emailId, fileId]);

  const handleTreatmentPublished = useCallback(() => {
    queryClient.setQueryData(
      [...emailFileDetailsKeys.detail({ projectId, emailId, inboxEntryId, fileId })],
      (prev: any) => {
        return {
          ...prev,
          data: {
            ...prev.data,
            'allow-close': {
              allowed: true,
            },
            'allowed-status-changes': [InboxEntryStatus.Closed],
            document: {
              ...prev.data.document,
              recorded: {
                date: moment().format(),
                name: `${loggedUser.data.firstName} ${loggedUser.data.lastName}`,
              },
            },
          },
        };
      }
    );
    handleActivityLogsUpdated();
  }, [inboxEntryId, projectId, emailId, fileId, loggedUser, handleActivityLogsUpdated]);

  return {
    sidebarActions: {
      recordFileMutation,
      markMiscellaneousMutation,
      markReadMutation,
      markReadMutationListener,
      updateStatusMutation,
      updateStatusMutationListener,
      tagFileWorkstreamMutation,
      duplicateFileMutation,
      createTask: {
        linkId: fileId,
        taskLinkKey: FILE_TASK_LINK_KEY.INBOX_EMAIL_ATTACHMENT_ID,
        onTaskCreated: handleTaskCreated,
        onTaskUpdated: handleTaskUpdated,
        onTaskDeleted: handleTaskDeleted,
      },
      onFileArchived: handleFileArchived,
      onCopyFileUrl: handleCopyFielUrl,
      onFileUrlCopied: handleActivityLogsUpdated,
      onFileUrlDownloaded: handleActivityLogsUpdated,
    },
    infoPanelQueries: {
      comments: {
        useCommentsQuery: () =>
          useEmailFileComments({
            projectId,
            emailId,
            inboxEntryId,
            fileId,
          }),
        onCommentAdded: handleCommentAdded,
      },
      useActivityLogsQuery: () =>
        useEmailFileActivityLogs({ projectId, fileId, emailId, inboxEntryId }),
    },
    treatmentEvents: {
      onDraftAction: handleActivityLogsUpdated,
      onTreatmentPublished: handleTreatmentPublished,
    },
  };
};

export default useEmailFileEntryViewerStore;
